import { bakePixels } from "./bake-pixels";
import { clipDuration, frameAt, statesForDir } from "./anim-set";
import type {
  AnimDir,
  AnimState,
  AnimStateName,
  ArtPack,
  BakeResult,
  SpriteTemplate,
} from "./types";

const packs = new Map<string, ArtPack>();

/**
 * Core art pipeline — pack registry + pure bake (no Phaser/DOM).
 */
export const ArtPipelineCore = {
  registerPack(pack: ArtPack): void {
    packs.set(pack.manifest.id, pack);
  },

  getPack(packId: string): ArtPack {
    const p = packs.get(packId);
    if (!p) throw new Error(`Art pack not registered: ${packId}`);
    return p;
  },

  hasPack(packId: string): boolean {
    return packs.has(packId);
  },

  listPackIds(): string[] {
    return [...packs.keys()];
  },

  listPackSpriteIds(packId: string): string[] {
    return [...this.getPack(packId).manifest.sprites];
  },

  bake(
    packId: string,
    spriteId: string,
    affiliationKey: string,
  ): BakeResult {
    const pack = this.getPack(packId);
    const spr = pack.sprites[spriteId];
    if (!spr) throw new Error(`Sprite '${spriteId}' missing in pack ${packId}`);
    const aff = pack.affiliation[affiliationKey];
    if (!aff) {
      throw new Error(
        `Affiliation '${affiliationKey}' missing in pack ${packId}`,
      );
    }
    return bakePixels(spr, pack.palette, aff);
  },

  /** Resolve clip frame index / done without baking pixels. */
  resolveAnimFrame(
    packId: string,
    spriteId: string,
    state: AnimStateName,
    timeSec: number,
    facing?: AnimDir,
  ): { frameIndex: number; clipDone: boolean; rows: string[]; dir: AnimDir } {
    const pack = this.getPack(packId);
    const anim = pack.anims[spriteId];
    if (!anim) throw new Error(`Anims missing for ${spriteId} in ${packId}`);
    const dir = facing ?? anim.dir;
    const states = statesForDir(anim, dir);
    // Extra named clips (pattern anims like "slam"/"brass"/"leap") fall back
    // to "attack" when a pack has not authored them; core states still throw.
    const clip = states[state] ?? states.attack;
    if (!clip) throw new Error(`State ${state} missing for ${spriteId}`);
    const { frameIndex, rows } = frameAt(clip, timeSec);
    const done = !clip.loop && timeSec >= clipDuration(clip);
    return { frameIndex, clipDone: done, rows, dir };
  },

  bakeAnimFrame(
    packId: string,
    spriteId: string,
    affiliationKey: string,
    state: AnimStateName,
    timeSec: number,
    facing?: AnimDir,
  ): {
    baked: BakeResult;
    frameIndex: number;
    clipDone: boolean;
    dir: AnimDir;
  } {
    const pack = this.getPack(packId);
    const { frameIndex, clipDone, rows, dir } = this.resolveAnimFrame(
      packId,
      spriteId,
      state,
      timeSec,
      facing,
    );
    const aff = pack.affiliation[affiliationKey];
    if (!aff) throw new Error(`Affiliation '${affiliationKey}' missing`);
    const tmp: SpriteTemplate = {
      id: `${spriteId}:${dir}:${state}:${frameIndex}`,
      size: rows.length,
      rows,
    };
    const baked = bakePixels(tmp, pack.palette, aff);
    return { baked, frameIndex, clipDone, dir };
  },

  animClipDuration(
    packId: string,
    spriteId: string,
    state: AnimStateName,
    facing?: AnimDir,
  ): number {
    const anim = this.getPack(packId).anims[spriteId];
    if (!anim) return 0.3;
    const clip = statesForDir(anim, facing ?? anim.dir)[state];
    if (!clip) return 0.3;
    return clipDuration(clip);
  },
};
